import { useState, useEffect, useCallback, useRef } from 'react';
import { CircleCheck as CheckCircle2, Activity, Save, Building2, Sparkles, Loader as Loader2, RefreshCw, CircleAlert as AlertCircle, RotateCcw, Newspaper } from 'lucide-react';
import { logAccountActivity } from '../../lib/activityLog';
import { useRealtimeStatus } from '../../lib/useRealtimeStatus';
import { canInvokeEdgeFn, releaseEdgeFn } from '../../lib/edgeFnGuard';
import { Sentry } from '../../lib/sentry';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import type { Account, ValuePyramid, ValueStatements, RAGStatus } from '../../types';
import RAGIndicator from '../common/RAGIndicator';
import CompanyProfileCard from './overview/CompanyProfileCard';
import IdentityAlert from './overview/IdentityAlert';
import RecentNewsCard from './overview/RecentNewsCard';
import TechStackCard from './overview/TechStackCard';
import TriggerRadarCard from './triggers/TriggerRadarCard';

interface Props {
  account: Account;
  pyramid: ValuePyramid | null;
  valueStatements: ValueStatements | null;
  vendorIntelPublished: boolean;
  onAccountUpdate: (account: Account) => void;
  isOwner?: boolean;
}

interface NewsRow { id: string; title: string; url: string; source: string; published_at: string | null; summary: string; }
interface ToolRow { id: string; tool_name: string; category: string; source: string; confidence: string; website_url: string; description: string; }
interface ActivityRow { id: string; activity_type: string; description: string; created_at: string; user_id: string | null; user_name?: string; }
interface OverviewSideDataResponse { news: NewsRow[]; activity: ActivityRow[]; }

// How old the stored news feed may get before the overview refreshes it on open.
const NEWS_MAX_AGE_MS = 6 * 60 * 60 * 1000;

interface OverviewTamResponse {
  overview_tam_min: number;
  overview_tam_max: number;
  overview_tam_confidence: 'low' | 'medium' | 'high';
  overview_tam_summary: string;
  overview_tam_assumptions: string[];
}

export default function OverviewTab({ account, pyramid, valueStatements, vendorIntelPublished, onAccountUpdate, isOwner = true }: Props) {
  const { apiGet, apiPost, apiPut } = useApi();
  const apiPostRef = useRef(apiPost);
  apiPostRef.current = apiPost;
  const apiGetRef = useRef(apiGet);
  apiGetRef.current = apiGet;
  const { organisation } = useAuth();
  const orgIdRef = useRef(organisation?.id);
  orgIdRef.current = organisation?.id;
  const [news, setNews] = useState<NewsRow[]>([]);
  const [tools, setTools] = useState<ToolRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [nextSteps, setNextSteps] = useState(account.next_steps);
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(account.enrichment_status === 'processing');
  const [fetchingNews, setFetchingNews] = useState(false);
  const [enrichError, setEnrichError] = useState('');
  const enrichLockRef = useRef(false);
  const enrichTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onAccountUpdateRef = useRef(onAccountUpdate);
  onAccountUpdateRef.current = onAccountUpdate;
  const accountRef = useRef(account);
  accountRef.current = account;

  const enrichDoneRef = useRef(false);
  const parallelRunIdRef = useRef(0);

  const ENRICH_TIMEOUT_MS = 180_000;

  const computeAndPersistTAM = useCallback(async (acct: Account) => {
    try {
      const orgId = orgIdRef.current;
      if (!orgId) return;
      const tam = await apiPostRef.current<OverviewTamResponse>(
        `/v1/account/${acct.id}/overview-tam`,
        { org_id: orgId },
      );
      onAccountUpdateRef.current({ ...acct, ...tam });
    } catch { /* non-blocking */ }
  }, []);

  const loadSideData = useCallback(async () => {
    const orgQuery = organisation?.id ? `?org_id=${encodeURIComponent(organisation.id)}` : '';
    const [sideDataRes, toolsRes] = await Promise.all([
      apiGet<OverviewSideDataResponse>(`/v1/account/${account.id}/overview-side-data${orgQuery}`).catch(() => ({ news: [] as NewsRow[], activity: [] as ActivityRow[] })),
      apiGet<{ tools: ToolRow[] }>(`/v1/account/${account.id}/tools${orgQuery}`).catch(() => ({ tools: [] as ToolRow[] })),
    ]);
    setNews(sideDataRes.news);
    setTools(toolsRes.tools);
    setActivity(sideDataRes.activity);
  }, [account.id, apiGet, organisation?.id]);

  useEffect(() => { loadSideData(); }, [loadSideData]);

  const loadSideDataRef = useRef(loadSideData);
  loadSideDataRef.current = loadSideData;
  const computeTAMRef = useRef(computeAndPersistTAM);
  computeTAMRef.current = computeAndPersistTAM;
  // deferring ref assignment until after fireNewsFetch is declared
  const fireNewsFetchRef = useRef<(acctId: string) => void>(() => {});

  const fireTriggerDetection = useCallback(async (acctId: string) => {
    try {
      await apiPostRef.current('/v1/detect-account-triggers', { account_id: acctId });
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'detect-account-triggers' } });
    }
  }, []);

  const triggerFiredRef = useRef(false);

  const tryFireTriggers = useCallback((runId: number, acctId: string) => {
    if (runId !== parallelRunIdRef.current) return;
    if (triggerFiredRef.current) return;
    if (enrichDoneRef.current) {
      triggerFiredRef.current = true;
      fireTriggerDetection(acctId);
    }
  }, [fireTriggerDetection]);

  const TERMINAL_ENRICHMENT_STATUSES = ['completed', 'error', 'needs_review'];

  useRealtimeStatus({
    table: 'accounts',
    filterColumn: 'id',
    filterValue: account.id,
    enabled: enriching,
    statusField: 'enrichment_status',
    terminalStatuses: TERMINAL_ENRICHMENT_STATUSES,
    fallbackIntervalMs: 3000,
    // Poll the latest account row through the signed AWS read instead of the
    // (stubbed) Supabase client. `GET /v1/account/{id}` returns the full account,
    // so the hook can read `enrichment_status` and feed `onUpdate` the same shape.
    // Uses the file's existing apiGet/orgId refs so the closure stays stable and
    // does not re-subscribe (same pattern as TriggerRadarCard/ResearchTab).
    fetcher: useCallback(async () => {
      const orgId = orgIdRef.current;
      if (!orgId) return null;
      const { account: fresh } = await apiGetRef.current<{ account: Account }>(
        `/v1/account/${account.id}?org_id=${encodeURIComponent(orgId)}`,
      );
      return fresh as unknown as Record<string, unknown>;
    }, [account.id]),
    onUpdate: useCallback((row: Record<string, unknown>) => {
      const data = row as unknown as Account;
      const status = data.enrichment_status;
      if (status === 'processing' || status === 'none' || status === undefined) return;
      if (enrichTimeoutRef.current) {
        clearTimeout(enrichTimeoutRef.current);
        enrichTimeoutRef.current = null;
      }
      setEnriching(false);
      enrichLockRef.current = false;
      releaseEdgeFn(`enrich-${data.id}`);
      onAccountUpdateRef.current(data);
      if (status === 'error') {
        setEnrichError((data as unknown as Record<string, unknown>).enrichment_error as string || 'Enrichment failed');
      } else if (status === 'needs_review') {
        setEnrichError((data as unknown as Record<string, unknown>).enrichment_error as string || 'Company identity could not be verified. Please check the data.');
      } else {
        setEnrichError('');
        loadSideDataRef.current();
        enrichDoneRef.current = true;
        tryFireTriggers(parallelRunIdRef.current, data.id);
        fireNewsFetchRef.current(data.id);
        computeTAMRef.current(data);
      }
    }, [tryFireTriggers]),
  });

  useEffect(() => {
    if (account.enrichment_status === 'processing') {
      setEnriching(true);
    }
  }, [account.enrichment_status]);

  useEffect(() => {
    return () => {
      if (enrichTimeoutRef.current) clearTimeout(enrichTimeoutRef.current);
    };
  }, []);

  // The feed is free to call, so news is refreshed whenever the stored copy has gone stale
  // rather than only on the very first visit -- an account fetched once on the day it was
  // enriched used to keep showing that one snapshot (often empty) forever.
  useEffect(() => {
    if (account.enrichment_status !== 'completed' || enriching) return;
    const fetchedAt = account.news_fetched_at ? new Date(account.news_fetched_at).getTime() : 0;
    if (Number.isFinite(fetchedAt) && Date.now() - fetchedAt < NEWS_MAX_AGE_MS) return;
    fireNewsFetchRef.current(account.id);
  }, [account.id, account.enrichment_status, account.news_fetched_at, enriching]);

  // Backfill TAM for already-enriched accounts missing TAM data
  useEffect(() => {
    if (account.enrichment_status === 'completed' && !account.overview_tam_max && !enriching) {
      computeTAMRef.current(account);
    }
  }, [account.id, account.enrichment_status, account.overview_tam_max, enriching]);

  const newsFetchLockRef = useRef(false);

  // The endpoint reads the RSS feed and stores the articles within the request, so completion
  // is simply the response -- no background run and no news_status poll to wait on.
  const fireNewsFetch = useCallback(async (acctId: string) => {
    if (newsFetchLockRef.current) return;
    newsFetchLockRef.current = true;
    setFetchingNews(true);
    try {
      const { news_fetched_at } = await apiPostRef.current<{ news_fetched_at: string }>(
        '/v1/fetch-account-news',
        { account_id: acctId },
      );
      await loadSideDataRef.current();
      onAccountUpdateRef.current({
        ...accountRef.current,
        news_fetched_at,
        news_status: 'completed',
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'fetch-account-news' } });
      // Record the attempt locally so a failing feed does not re-fire on every render.
      onAccountUpdateRef.current({
        ...accountRef.current,
        news_fetched_at: new Date().toISOString(),
        news_status: 'error',
      });
    } finally {
      setFetchingNews(false);
      newsFetchLockRef.current = false;
    }
  }, []);
  fireNewsFetchRef.current = fireNewsFetch;

  // `override` carries an account the caller has just persisted (identity correction), whose
  // fields this render's `account` prop does not have yet -- without it the re-run would be
  // sent with the website the user just replaced.
  const startEnrich = (forceRefresh: boolean, override?: Account) => {
    const target = override ?? account;
    if (enrichLockRef.current) return;
    if (!canInvokeEdgeFn(`enrich-${account.id}`)) return;
    enrichLockRef.current = true;
    setEnriching(true);
    setEnrichError('');

    parallelRunIdRef.current += 1;
    enrichDoneRef.current = false;
    triggerFiredRef.current = false;

    if (enrichTimeoutRef.current) clearTimeout(enrichTimeoutRef.current);
    enrichTimeoutRef.current = setTimeout(() => {
      enrichTimeoutRef.current = null;
      setEnriching(false);
      enrichLockRef.current = false;
      releaseEdgeFn(`enrich-${account.id}`);
      setEnrichError('Enrichment timed out. Please try again.');
    }, ENRICH_TIMEOUT_MS);

    const payload: Record<string, unknown> = {
      account_id: target.id,
      company_name: target.company_name,
      website: target.website_url,
      website_url: target.website_url,
      ticker: target.ticker_symbol,
      ticker_symbol: target.ticker_symbol,
    };
    if (forceRefresh) payload.force_refresh = true;
    apiPostRef.current('/v1/enrich-account', payload).catch((err: unknown) => {
      Sentry.captureException(err, { tags: { context: 'enrich-account' } });
      if (enrichTimeoutRef.current) { clearTimeout(enrichTimeoutRef.current); enrichTimeoutRef.current = null; }
      setEnriching(false);
      enrichLockRef.current = false;
      releaseEdgeFn(`enrich-${account.id}`);
      setEnrichError(err instanceof Error ? err.message : 'Failed to start enrichment');
    });

    logAccountActivity(target.id, forceRefresh ? 'force_refresh_started' : 'enrichment_started', `${forceRefresh ? 'Force refreshed' : 'Started'} account intelligence for ${target.company_name}`);
  };

  const handleEnrich = () => startEnrich(false);
  const handleForceRefresh = () => startEnrich(true);
  const handleIdentityRerun = (corrected?: Account) => startEnrich(true, corrected);

  const saveNextSteps = async () => {
    setSaving(true);
    try {
      if (!organisation) return;
      const { account: data } = await apiPut<{ account: Account }>(
        `/v1/account/${account.id}?org_id=${encodeURIComponent(organisation.id)}`,
        { next_steps: nextSteps },
      );
      logAccountActivity(account.id, 'next_steps_updated', `Updated next steps for ${account.company_name}`);
      onAccountUpdate(data);
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'save_next_steps' } });
    } finally {
      setSaving(false);
    }
  };

  const hasEnriched = account.enrichment_status === 'completed';
  const needsReview = account.enrichment_status === 'needs_review';
  const [useCaseStatus, setUseCaseStatus] = useState<string>('pending');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const orgQuery = orgIdRef.current ? `?org_id=${encodeURIComponent(orgIdRef.current)}` : '';
        const status = await apiGetRef.current<{ use_case_status: string | null }>(
          `/v1/account/${account.id}/overview-status${orgQuery}`,
        );
        if (cancelled) return;
        if (status.use_case_status) setUseCaseStatus(status.use_case_status);
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [account.id]);

  const statuses: { label: string; status: RAGStatus }[] = [
    { label: 'Company Profile', status: hasEnriched ? 'green' : enriching ? 'amber' : 'red' },
    { label: 'Value Pyramid', status: pyramid?.status === 'completed' ? 'green' : pyramid?.status === 'processing' ? 'amber' : 'red' },
    { label: 'Strategic Goals', status: pyramid?.strategic_goals_status === 'completed' ? 'green' : pyramid?.strategic_goals_status === 'processing' ? 'amber' : 'red' },
    { label: 'Value Statements', status: valueStatements?.status === 'completed' ? 'green' : valueStatements?.status === 'processing' ? 'amber' : 'red' },
    { label: 'Trigger Signals', status: account.signals_status === 'completed' ? 'green' : account.signals_status === 'processing' ? 'amber' : 'red' },
    { label: 'Use Cases', status: useCaseStatus === 'completed' ? 'green' : useCaseStatus === 'processing' ? 'amber' : 'red' },
  ];

  return (
    <div className="space-y-8">
      <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-app-ink flex items-center gap-2">
            <Building2 className="w-5 h-5 text-forest-600" /> Company Profile
          </h2>
          <div className="flex items-center gap-2">
            {isOwner && hasEnriched && account.data_source === 'cached' && !enriching && (
              <button
                onClick={handleForceRefresh}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-app-line text-app-muted hover:bg-app-canvas hover:border-app-line-strong transition"
                title="Bypass cache and run fresh research from the web"
              >
                <RotateCcw className="w-4 h-4" />
                Force Fresh Research
              </button>
            )}
            {isOwner && (
            <button
              onClick={handleEnrich}
              disabled={enriching}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                enriching
                  ? 'bg-app-canvas text-app-muted cursor-not-allowed'
                  : 'bg-forest-600 text-white hover:bg-forest-700 shadow-none hover:shadow'
              }`}
            >
              {enriching ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Researching...
                </>
              ) : hasEnriched ? (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Refresh Intelligence
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Populate Account Intelligence
                </>
              )}
            </button>
            )}
          </div>
        </div>
        <IdentityAlert
          account={account}
          isOwner={isOwner}
          enriching={enriching}
          onAccountUpdate={onAccountUpdate}
          onRerun={handleIdentityRerun}
        />
        {enrichError && !needsReview && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg text-sm text-app-critical">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {enrichError}
          </div>
        )}
        {enriching && (
          <div className="mb-4 flex items-center gap-3 p-4 bg-forest-50 border border-forest-100 rounded-lg">
            <Loader2 className="w-5 h-5 text-forest-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-forest-800">Researching {account.company_name}...</p>
              <p className="text-xs text-forest-600 mt-0.5">This typically takes 30 to 60 seconds. You can leave this page and return when the results are ready.</p>
            </div>
          </div>
        )}
        <CompanyProfileCard account={account} />
      </div>

      <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
        <h2 className="text-base font-semibold text-app-ink mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-forest-600" /> Pipeline Status
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {statuses.map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-app-canvas rounded-lg">
              <RAGIndicator status={item.status} />
              <span className="text-sm text-app-ink font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {fetchingNews && (
        <div className="flex items-center gap-3 p-4 bg-app-focus/10 border border-app-focus rounded-lg">
          <Newspaper className="w-5 h-5 text-app-focus flex-shrink-0" />
          <Loader2 className="w-4 h-4 text-app-focus animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-app-focus">Fetching latest news...</p>
            <p className="text-xs text-app-focus mt-0.5">Reading the news feed for {account.company_name}.</p>
          </div>
        </div>
      )}

      <TriggerRadarCard account={account} onAccountUpdate={onAccountUpdate} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentNewsCard news={news} hasEnriched={hasEnriched} accountId={account.id} newsFetchedAt={account.news_fetched_at} fetchingNews={fetchingNews} onRefresh={() => fireNewsFetch(account.id)} />
        <TechStackCard tools={tools} hasEnriched={hasEnriched} />
      </div>

      <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
        <h2 className="text-base font-semibold text-app-ink mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-forest-600" /> Recent Activity
        </h2>
        {activity.length === 0 ? (
          <p className="text-sm text-app-muted text-center py-4">No activity recorded yet</p>
        ) : (
          <div className="space-y-2">
            {activity.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-3 bg-app-canvas rounded-lg">
                <div className="w-2 h-2 bg-forest-400 rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-app-ink">{item.description}</p>
                  {item.user_name && (
                    <p className="text-[11px] text-app-muted mt-0.5">{item.user_name}</p>
                  )}
                </div>
                <span className="text-xs text-app-muted flex-shrink-0">{new Date(item.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-app-ink">Next Steps</h2>
          <button onClick={saveNextSteps} disabled={saving} className="btn-secondary !py-2 !px-3 !text-xs">
            <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : 'Save'}
          </button>
        </div>
        <textarea value={nextSteps} onChange={e => setNextSteps(e.target.value)} placeholder="Document your next steps for this account..." className="input-field min-h-[120px] resize-y" />
      </div>
    </div>
  );
}
